import '../styles/LegalPage.css';

const PRIVACY_POLICY = {
  title: 'Privacy Policy',
  effective: 'Effective Date: 22 May 2026  |  Last Updated: 22 May 2026  |  Version 1.0',
  commitments: [
    'Your financial data is yours. You retain all rights to your Tally data and audit reports.',
    'We do not use your data to train any AI or machine learning models.',
    'Tally files are deleted immediately after your audit report is generated.',
    'We store only the final audit report — nothing more.',
    'You have the right to request deletion of your data at any time.',
    'By using AuditPros.ai, you agree to this Privacy Policy.',
  ],
  sections: [
    {
      heading: '1. Introduction',
      body: 'This Privacy Policy describes how Speedstar AI Labs Private Limited (\'AuditPros\', \'we\', \'us\', or \'our\'), the company behind AuditPros.ai, collects, uses, stores, and protects personal and financial data when you access or use our platform.\n\nAuditPros.ai is India\'s two-sided audit collaboration platform — connecting Chartered Accountants (CAs) and their MSME clients for seamless, AI-assisted audit workflows. Given the sensitive nature of financial data processed on our platform, we hold data privacy to the highest standard.\n\nBy accessing or using AuditPros.ai, creating an account, or uploading any data, you agree to this Privacy Policy. If you do not agree with any part of this Policy, please discontinue use of our platform.',
    },
    {
      heading: '2. Who We Are',
      table: [
        ['Legal Entity', 'Speedstar AI Labs Private Limited'],
        ['Platform', 'AuditPros.ai'],
        ['Registered Office', 'Bangalore, Karnataka, India (Incorporated January 2026)'],
        ['Recognition', 'DPIIT Recognised Startup'],
        ['Grievance Officer', 'Sameep Bansal, Founder'],
        ['Grievance Email', 'support@speedstar.ai'],
        ['Response Time', 'Within 30 days of receipt of complaint'],
      ],
    },
    {
      heading: '3. Scope and Applicability',
      body: 'This Policy applies to:',
      list: [
        'Chartered Accountants (CAs) who register on AuditPros.ai to manage audit engagements',
        'MSME clients and businesses whose data is uploaded or shared by their CA',
        'Visitors to the AuditPros.ai website',
        'Any individual or entity using our platform, APIs, or related services',
      ],
      body2: 'This Policy covers data collected through our website, web application, mobile application (if applicable), and any integrations including Tally ERP.',
    },
    {
      heading: '4. Data We Collect',
      subsections: [
        {
          sub: '4.1 Account and Identity Data',
          body: 'For CAs: Full name, ICAI Membership Number, firm name, email address, mobile number, PAN, GST details, profile photograph.\n\nFor MSME Clients: Business name, PAN, GSTIN, director/proprietor name, registered address, contact email and phone.\n\nSubscription plan, billing address, GST number for invoicing purposes.',
        },
        {
          sub: '4.2 Tally ERP and Financial Data',
          body: 'This is the most sensitive category of data on our platform and is subject to our strictest handling rules:',
          list: [
            'Tally export files including ledgers, trial balance, P&L statements, balance sheet, vouchers, and journal entries',
            'GST-related data, tax computations, and bank transaction records derived from Tally exports',
            'Audit flags, anomalies, and observations generated by our rule engine on the above data',
          ],
          highlight: 'Critical: Tally File Retention Policy\n\nTally data files uploaded to AuditPros.ai are processed in-memory for audit rule execution and AI report generation only. Once the final audit report has been successfully generated, the original Tally file is permanently deleted from our systems. We retain only the final audit report. We do not archive, back up, or store raw Tally files beyond this processing window.',
        },
        {
          sub: '4.3 Audit Reports and Work Product',
          list: [
            'AI-generated audit reports, findings, observations, and recommendations',
            'CA annotations, comments, and sign-off decisions made within the platform',
            'Engagement history, status logs, and version history of reports',
          ],
        },
        {
          sub: '4.4 Usage and Technical Data',
          list: [
            'IP address, browser type, operating system, device identifiers',
            'Session data, pages visited, features accessed, time and duration of visits',
            'Audit trail logs — records of who accessed, uploaded, or modified data and when',
            'Error reports and diagnostic data for service improvement',
          ],
        },
        {
          sub: '4.5 Communications Data',
          list: [
            'Emails or messages sent to our support team',
            'Feedback, bug reports, and survey responses',
          ],
        },
      ],
    },
    {
      heading: '5. How We Use Your Data',
      body: 'We use data collected on AuditPros.ai strictly for the purposes listed below. We do not use your data for any purpose not described in this Policy without obtaining separate consent.',
      table: [
        ['Audit Rule Execution', 'Running 100+ deterministic audit checks on your Tally data to identify anomalies, flags, and compliance gaps'],
        ['AI Report Generation', 'Generating structured audit reports using AI, based on the processed Tally data'],
        ['Platform Operations', 'Managing your account, subscriptions, billing, and CA-client engagements'],
        ['Collaboration', 'Enabling CA-client communication, annotation, and report review within the platform'],
        ['Security and Integrity', 'Detecting fraud, unauthorised access, and maintaining platform security'],
        ['Legal Compliance', 'Meeting obligations under Indian law including DPDP Act 2023, IT Act 2000, and GST laws'],
        ['Product Improvement', 'Analysing aggregated, anonymised usage patterns to improve platform features'],
        ['Customer Support', 'Responding to queries, resolving disputes, and providing technical assistance'],
      ],
      body2: 'We will never use your Tally data, financial records, or audit reports for advertising, profiling, or sale to third parties.',
    },
    {
      heading: '6. Legal Basis for Processing',
      list: [
        'Digital Personal Data Protection Act, 2023 (DPDP Act) — primary framework for all personal data processing in India',
        'Information Technology Act, 2000 and IT (Amendment) Act, 2008 — governing electronic records and data security',
        'Contractual necessity — data processed to fulfil our obligations under our Terms of Service',
        'Consent — you provide explicit consent by accepting this Policy upon account creation and use of the platform',
        'Legitimate interest — usage analytics and security monitoring to maintain platform integrity',
        'Legal obligation — records required by tax, corporate, or regulatory authorities',
      ],
      body2: 'For users outside India (if applicable), we additionally comply with the General Data Protection Regulation (GDPR) where relevant, using Standard Contractual Clauses for international data transfers.',
    },
    {
      heading: '7. Data Roles — Two-Sided Platform',
      table: [
        ['Speedstar AI Labs Pvt Ltd', 'Data Fiduciary (Controller) for account, usage, and platform operational data. Data Processor acting on CA instructions for Tally and client financial data.'],
        ['Chartered Accountant (CA)', 'Data Fiduciary for their clients financial and business data. The CA is responsible for obtaining valid consent from their MSME clients before uploading data to AuditPros.ai.'],
        ['MSME / Client Business', 'Data Principal. Has direct rights over their personal and financial data under the DPDP Act 2023.'],
      ],
      body2: 'CAs are responsible for ensuring they have the lawful authority to upload client data to AuditPros.ai. By uploading client data, the CA represents that they have obtained all necessary consents and authorisations from their client.',
    },
    {
      heading: '8. Data Retention',
      table: [
        ['Tally Export Files', 'Deleted immediately and permanently after the audit report is successfully generated. Not retained, backed up, or archived.'],
        ['AI-Generated Audit Reports', 'Retained for the duration of the active engagement plus 7 years, in line with statutory audit record-keeping requirements.'],
        ['Account and Identity Data', 'Retained while the account is active and for 3 years after account closure, for legal compliance purposes.'],
        ['Usage Logs and Audit Trails', 'Retained for 2 years for security monitoring and legal compliance.'],
        ['Billing and Transaction Records', 'Retained for 8 years as required under the Income Tax Act and GST laws.'],
        ['Support Communications', 'Retained for 2 years from date of resolution.'],
      ],
      body2: 'Upon account termination or written request, we will delete all personal data within 30 days, except where retention is required by law.',
    },
    {
      heading: '9. Data Sharing and Disclosure',
      body: 'We do not sell, rent, or trade your data. We share data only in the following limited circumstances:',
      subsections: [
        {
          sub: '9.1 Sub-Processors and Technology Partners',
          table: [
            ['Anthropic (Claude AI)', 'AI model inference for audit report generation. Processed under strict API agreements. Anthropic does not use your data to train models.'],
            ['Cloud Infrastructure', 'Azure for hosting, storage, and compute. Data residency is India.'],
            ['Payment Gateway', 'RBI Authorised Licenced PA or equivalent for processing subscription payments. Only billing data is shared; financial audit data is never passed.'],
            ['Email / Notification Service', 'Transactional emails and platform alerts only.'],
          ],
        },
        {
          sub: '9.2 Regulatory and Legal Disclosure',
          body: 'We may disclose data to government authorities, courts, or regulators where required by law, court order, or to protect the rights and safety of our users or the public. We will notify you of such disclosure where legally permitted to do so.',
        },
        {
          sub: '9.3 Business Transfer',
          body: 'In the event of a merger, acquisition, or sale of assets, your data may be transferred to the successor entity. We will notify you in advance and ensure equivalent privacy protections are maintained.',
        },
      ],
    },
    {
      heading: '10. Data Security',
      list: [
        'AES-256 encryption for all data at rest',
        'TLS 1.2 or higher for all data in transit',
        'Role-based access controls — CAs can only access their own clients data; MSME clients can only access their own records',
        'Immutable audit trail logging all data access, uploads, and modifications with timestamps and user identifiers',
        'Tally files processed in isolated, ephemeral compute environments and deleted after report generation',
        'Regular security assessments, penetration testing, and vulnerability management',
        'Employee access limited to authorised personnel on a need-to-know basis',
        'Incident response procedures for breach detection and containment',
      ],
      body2: 'In the event of a personal data breach affecting your data, we will notify the Data Protection Board of India (DPBI) and affected users as required under the DPDP Act 2023, without undue delay.',
    },
    {
      heading: '11. Data Residency',
      body: 'All data collected and processed through AuditPros.ai is stored on servers located within India. We do not transfer personal data or financial data outside India except where explicitly authorised by you or required by law, and only with appropriate safeguards in place.',
    },
    {
      heading: '12. AI Usage and Model Training',
      body: 'We do not use Customer Data to train any AI or machine learning models.\n\nAI inference (report generation) is performed via the Anthropic Claude API under strict contractual terms that prohibit use of our customers data for model training. Your Tally data, audit findings, and reports remain exclusively yours and are never used to improve third-party AI systems.\n\nWe may use aggregated, anonymised, and de-identified statistical data (e.g., the number of audit flags detected across sectors) for internal product research and marketing. This data cannot be used to identify any individual, business, or CA firm.',
    },
    {
      heading: '13. Your Rights under the DPDP Act 2023',
      body: 'As a Data Principal, you have the following rights under the Digital Personal Data Protection Act, 2023:',
      table: [
        ['Right to Access', 'Request a summary of the personal data we hold about you and how it is being processed.'],
        ['Right to Correction', 'Request correction of inaccurate or incomplete personal data.'],
        ['Right to Erasure', 'Request deletion of your personal data, subject to legal retention obligations.'],
        ['Right to Grievance Redressal', 'File a complaint with our Grievance Officer; we will respond within 30 days.'],
        ['Right to Nominate', 'Nominate another individual to exercise your rights in the event of your death or incapacity.'],
        ['Right to Withdraw Consent', 'Withdraw consent for data processing at any time; this does not affect processing already carried out.'],
      ],
      body2: 'To exercise any of these rights, contact our Grievance Officer at support@speedstar.ai. We will verify your identity and respond within 30 days.',
    },
    {
      heading: '14. Cookies and Tracking',
      table: [
        ['Essential Cookies', 'Required for platform functionality — login sessions, security tokens. Cannot be disabled.'],
        ['Analytics Cookies', 'Aggregate usage analytics to improve platform performance. Opt-out available in settings.'],
        ['Preference Cookies', 'Remember your language, display, and notification settings. Opt-out available.'],
      ],
      body2: 'We do not use advertising or tracking cookies. We do not serve ads on AuditPros.ai.',
    },
    {
      heading: '15. Data of Minors',
      body: 'AuditPros.ai is designed for professional business use and is not intended for individuals under the age of 18. We do not knowingly collect personal data from minors. If you believe a minor has provided data to us, please contact support@speedstar.ai and we will delete it promptly.',
    },
    {
      heading: '16. Third-Party Links and Integrations',
      body: 'AuditPros.ai may contain links to third-party websites or integrate with third-party services (e.g., Tally ERP, MCA portal, GSTN). This Policy does not apply to those third-party platforms. We encourage you to review their privacy policies before sharing any data with them.',
    },
    {
      heading: '17. Changes to This Policy',
      body: 'We may update this Privacy Policy periodically to reflect changes in our practices, technology, legal requirements, or business operations. When we make material changes, we will:',
      list: [
        'Post the updated Policy on auditpros.ai/privacy with a revised effective date',
        'Notify you via email or in-app notification at least 14 days before changes take effect',
        'Where required by law, seek fresh consent for significant changes in processing purpose',
      ],
      body2: 'Continued use of AuditPros.ai after the effective date constitutes acceptance of the updated Policy.',
    },
    {
      heading: '18. Governing Law and Jurisdiction',
      body: 'This Privacy Policy is governed by the laws of India, including the Digital Personal Data Protection Act 2023, the Information Technology Act 2000, and applicable rules thereunder. Any disputes arising from this Policy shall be subject to the exclusive jurisdiction of courts in Bangalore, Karnataka, India.',
    },
    {
      heading: '19. Contact and Grievance Officer',
      table: [
        ['Grievance Officer', 'Sameep Bansal, Founder — Speedstar AI Labs Private Limited'],
        ['Email', 'support@speedstar.ai'],
        ['Response Window', '30 days from date of receipt'],
        ['Registered Address', 'Speedstar AI Labs Private Limited, Bangalore, Karnataka, India'],
      ],
      body2: 'If you are not satisfied with our response, you may escalate your complaint to the Data Protection Board of India once constituted under the DPDP Act 2023.',
    },
  ],
};

const TERMS_OF_SERVICE = {
  title: 'Terms of Service',
  effective: 'Effective Date: 22 May 2026  |  Last Updated: 22 May 2026  |  Version 1.0 (Beta)',
  notice: {
    heading: 'Beta Programme Notice',
    items: [
      'AuditPros.ai is currently in Beta. The platform is available free of charge during this phase.',
      'Features, workflows, and interfaces may change without prior notice as we improve the product.',
      'We may take the platform offline for maintenance, upgrades, or enhancements at any time.',
      'Beta users accept that service continuity is not guaranteed during this phase.',
      'Commercial subscription pricing will be introduced when we exit Beta, with advance notice.',
    ],
  },
  commitments: [
    'Beta access is completely free. No credit card required.',
    'Tally files are deleted immediately after your audit report is generated.',
    'Your data is never used to train AI models.',
    'AI-generated reports are tools for CA review — not standalone audit opinions.',
    'By using AuditPros.ai, you agree to these Terms of Service.',
  ],
  sections: [
    {
      heading: '1. Acceptance of Terms',
      body: 'These Terms of Service constitute a legally binding agreement between you and Speedstar AI Labs Private Limited (\'AuditPros\', \'we\', \'us\', or \'our\'), the company operating AuditPros.ai.\n\nBy accessing or using AuditPros.ai — including creating an account, uploading data, or simply browsing the platform — you confirm that you have read, understood, and agree to be bound by these Terms and our Privacy Policy (available at auditpros.ai/privacy), which is incorporated herein by reference.\n\nIf you are using AuditPros.ai on behalf of a firm, company, or organisation, you represent that you have the authority to bind that entity to these Terms. If you do not agree, please discontinue use immediately.',
    },
    {
      heading: '2. Description of Service',
      body: 'AuditPros.ai is India\'s two-sided audit collaboration platform that enables:',
      list: [
        'Chartered Accountants (CAs) to upload client Tally ERP data, run automated audit rule checks, generate AI-assisted audit reports, and manage client engagements end-to-end',
        'MSME businesses and clients to access and review audit findings shared by their CA through the platform',
      ],
      body2: 'The platform is powered by deterministic audit rules and AI-assisted report generation (via Anthropic\'s Claude API). AuditPros.ai is a professional productivity tool and does not provide legal, financial, or statutory audit opinions.',
      highlight: 'Important: Beta Phase\n\nDuring the Beta phase, AuditPros.ai is provided free of charge. We may add, remove, or modify features at any time. We may also take the platform offline — with or without advance notice — for maintenance, upgrades, bug fixes, or enhancements.',
    },
    {
      heading: '3. User Eligibility and Registration',
      subsections: [
        {
          sub: '3.1 Eligibility',
          body: 'To use AuditPros.ai, you must:',
          list: [
            'Be at least 18 years of age',
            'If registering as a CA: hold a valid ICAI Membership Number and be in good standing with the Institute',
            'If registering as an MSME client: be an authorised representative of the business entity',
            'Have the legal capacity to enter into binding agreements under Indian law',
          ],
        },
        {
          sub: '3.2 Account Registration',
          list: [
            'You must provide accurate, complete, and current registration information',
            'You are responsible for maintaining the confidentiality of your login credentials',
            'You must notify us immediately at support@speedstar.ai if you suspect unauthorised access to your account',
            'One account per CA firm or per MSME entity; multiple users may be added within an account as permitted by your plan',
            'We reserve the right to reject or suspend registrations at our discretion',
          ],
        },
      ],
    },
    {
      heading: '4. User Roles and Responsibilities',
      subsections: [
        {
          sub: '4.1 Chartered Accountants (CAs)',
          body: 'By using AuditPros.ai, CAs agree that they:',
          list: [
            'Have obtained explicit authorisation from each MSME client before uploading their financial data to the platform',
            'Are solely responsible for the accuracy, completeness, and authenticity of all Tally data uploaded',
            'Remain professionally responsible for all audit judgements, sign-offs, and filings — the platform does not discharge any statutory obligation',
            'Will not upload data belonging to a client for whom they no longer hold a valid engagement mandate',
            'Will comply with all applicable ICAI guidelines regarding client data confidentiality and professional ethics',
          ],
        },
        {
          sub: '4.2 MSME Clients and Business Users',
          list: [
            'You authorise your CA to upload your financial and business data to AuditPros.ai on your behalf',
            'You are responsible for the accuracy of the source data provided to your CA',
            'You may access reports and findings shared with you by your CA within the platform',
            'You acknowledge that your CA, not AuditPros.ai, is your primary point of accountability for audit work',
          ],
        },
        {
          sub: '4.3 Role of AuditPros.ai',
          list: [
            'We provide the technology platform and infrastructure for audit collaboration',
            'We process Tally data to execute audit rule checks and generate AI-assisted reports',
            'We do not review, validate, or take professional responsibility for the accuracy of uploaded data or the conclusions drawn from it',
            'We are a technology service provider, not an audit firm or professional services entity',
          ],
        },
      ],
    },
    {
      heading: '5. Acceptable Use Policy',
      body: 'You agree not to use AuditPros.ai to:',
      list: [
        'Upload false, manipulated, fabricated, or fraudulent financial data',
        'Impersonate any individual, CA firm, or business entity',
        'Attempt to gain unauthorised access to another user\'s account or data',
        'Reverse-engineer, decompile, or attempt to extract the source code or audit rule logic of the platform',
        'Scrape, crawl, or systematically extract data from the platform using automated tools',
        'Use the platform in any manner that violates applicable Indian law, including the IT Act 2000, DPDP Act 2023, PMLA 2002, or any ICAI regulation',
        'Upload data on behalf of a client without their knowledge or authorisation',
        'Share your account credentials with unauthorised individuals',
        'Introduce malware, viruses, or malicious code into the platform',
        'Use AI-generated reports as standalone audit opinions without professional CA review and sign-off',
      ],
      body2: 'Violation of this Acceptable Use Policy may result in immediate account suspension or termination, without refund, and may be reported to relevant authorities.',
    },
    {
      heading: '6. Beta Phase — Free Access and Service Availability',
      subsections: [
        {
          sub: '6.1 Free Access During Beta',
          body: 'AuditPros.ai is provided free of charge during the Beta phase. There are no subscription fees, hidden charges, or payment obligations for Beta users.',
          table: [
            ['Beta Pricing', 'Free — no credit card, no payment required'],
            ['Features Available', 'All features released during Beta phase'],
            ['Beta Duration', 'Until we formally announce General Availability (GA)'],
            ['Transition to Paid', '30 days advance notice before commercial pricing takes effect'],
            ['Data at GA', 'Beta accounts and their audit reports will be migrated to the paid tier or retrievable before deletion'],
          ],
        },
        {
          sub: '6.2 Service Availability and Downtime',
          body: 'During the Beta phase, we do not provide any uptime guarantee or Service Level Agreement (SLA). Specifically:',
          list: [
            'We may take AuditPros.ai offline at any time for maintenance, performance improvements, feature additions, bug fixes, infrastructure upgrades, or security patches',
            'Planned downtime will be communicated via email or in-app notification where reasonably possible, but is not guaranteed',
            'Unplanned downtime may occur without notice',
            'We are not liable for any loss, inconvenience, or damages arising from platform unavailability during the Beta phase',
          ],
        },
      ],
    },
    {
      heading: '7. Intellectual Property',
      subsections: [
        {
          sub: '7.1 AuditPros Intellectual Property',
          body: 'All rights in and to AuditPros.ai — including the platform, audit rule engine, AI report generation pipeline, user interface, branding, trademarks, and underlying technology — are owned exclusively by Speedstar AI Labs Private Limited. Nothing in these Terms transfers any IP rights to you.',
        },
        {
          sub: '7.2 Your Data — Ownership and Licence',
          body: 'You retain full ownership of all data you upload to AuditPros.ai, including Tally files, business information, and generated audit reports. By uploading data, you grant AuditPros.ai a limited, non-exclusive, royalty-free licence to process your data solely for the purpose of providing the service to you.\n\nWe do not use your data for advertising, profiling, benchmarking against other users, or training any AI or machine learning models. Your financial data remains yours.',
        },
        {
          sub: '7.3 Feedback and Suggestions',
          body: 'If you provide feedback, suggestions, or ideas about AuditPros.ai, you grant us the right to use such feedback without restriction or compensation to you. We are not obligated to act on any feedback.',
        },
      ],
    },
    {
      heading: '8. Data Handling and Tally File Policy',
      body: 'Our data handling practices are governed by our Privacy Policy. Key commitments are restated here for clarity:',
      table: [
        ['Tally Files', 'Deleted permanently and immediately after the audit report is successfully generated. Not backed up, archived, or retained in any form.'],
        ['Audit Reports', 'Retained as long as your account is active and for 7 years post-engagement, in line with statutory requirements.'],
        ['AI Model Training', 'Your data is never used to train any AI or machine learning model.'],
        ['Data Residency', 'All data is stored on servers located within India.'],
        ['Third-Party Access', 'Your financial data is never sold, rented, or shared with third parties for commercial purposes.'],
      ],
    },
    {
      heading: '9. AI-Generated Reports — Important Disclaimers',
      highlight: 'AI-generated audit reports produced by AuditPros.ai are professional tools designed to assist Chartered Accountants in their work. They are NOT standalone audit opinions, statutory reports, or professional certifications of any kind.',
      body: 'By using AuditPros.ai, you acknowledge and agree that:',
      list: [
        'All AI-generated reports must be reviewed, validated, and approved by a qualified Chartered Accountant before being relied upon, shared with clients, or used for any regulatory or statutory purpose',
        'The CA remains solely and entirely responsible for professional judgement, audit conclusions, sign-offs, and compliance with ICAI standards and applicable law',
        'Report accuracy is entirely dependent on the quality and completeness of the Tally data uploaded',
        'AuditPros.ai does not verify, certify, or endorse any financial data uploaded to the platform',
        'AI findings are probabilistic and rule-based in nature; they may produce false positives or miss issues not covered by the audit rule set',
        'AuditPros.ai is not liable for any professional, legal, regulatory, or financial consequence arising from the use of or reliance on AI-generated reports',
      ],
    },
    {
      heading: '10. Confidentiality',
      subsections: [
        {
          sub: '10.1 Our Obligations',
          body: 'We treat all user data, Tally files, and audit reports as strictly confidential. We will not disclose your data to any third party except as described in our Privacy Policy or as required by law.',
        },
        {
          sub: '10.2 Your Obligations',
          list: [
            'Keep all audit reports, findings, and platform-generated insights confidential and share them only with authorised parties',
            'Not disclose client financial data to any third party without the client\'s explicit consent',
            'Maintain the confidentiality of your account credentials and not share them with unauthorised individuals',
            'For CAs: comply with ICAI guidelines on client data confidentiality and professional secrecy',
          ],
        },
      ],
    },
    {
      heading: '11. Limitation of Liability',
      body: 'To the maximum extent permitted by applicable Indian law:',
      list: [
        'AuditPros.ai is provided on an as-is and as-available basis, particularly during the Beta phase, without any warranty of fitness for a particular purpose, merchantability, or uninterrupted availability',
        'We are not liable for any indirect, consequential, incidental, punitive, or special damages arising from your use of or inability to use the platform',
        'We are not liable for any loss arising from inaccurate, incomplete, or manipulated Tally data uploaded by users',
        'We are not liable for professional or regulatory consequences arising from a CA\'s reliance on AI-generated reports without independent professional review',
        'We are not liable for any loss of data resulting from platform downtime, technical failure, or force majeure events',
      ],
      body2: 'During the Beta phase, since the service is provided free of charge, our aggregate liability to you for any claim arising from these Terms shall not exceed INR 0 (zero). Post-Beta, our aggregate liability will be capped at the subscription fees paid by you in the three (3) months immediately preceding the event giving rise to the claim.',
    },
    {
      heading: '12. Indemnification',
      body: 'You agree to indemnify, defend, and hold harmless Speedstar AI Labs Private Limited and its directors, officers, employees, and agents from and against any claims, damages, penalties, losses, and expenses (including reasonable legal fees) arising from:',
      list: [
        'Your breach of these Terms or our Privacy Policy',
        'Your violation of any applicable law or regulation',
        'Your upload of false, fabricated, or unauthorised financial data',
        'Your failure to obtain proper client authorisation before uploading their data',
        'Any professional negligence or misconduct in your use of AI-generated reports',
        'Infringement of any third-party intellectual property or privacy rights through your use of the platform',
      ],
    },
    {
      heading: '13. Term and Termination',
      subsections: [
        { sub: '13.1 Duration', body: 'These Terms are effective from the date you first access AuditPros.ai and continue until your account is terminated by either party.' },
        { sub: '13.2 Termination by You', body: 'You may terminate your account at any time by contacting support@speedstar.ai. During Beta, there are no fees or penalties for leaving.' },
        {
          sub: '13.3 Termination by Us',
          body: 'We may suspend or terminate your account, with or without notice, if you:',
          list: [
            'Violate any provision of these Terms or our Privacy Policy',
            'Upload false, fraudulent, or manipulated data',
            'Engage in conduct that harms other users, the platform, or third parties',
            'Misrepresent your identity or professional credentials',
          ],
          body2: 'We also reserve the right to discontinue the Beta programme entirely at any time, with reasonable advance notice where practicable.',
        },
        {
          sub: '13.4 Effect of Termination',
          body: 'Upon termination:',
          list: [
            'Your access to the platform ceases immediately',
            'You may request an export of your audit reports within 30 days of termination notice; after this period, data will be deleted per our Privacy Policy',
            'Tally files, if any are pending, will be deleted immediately',
            'Provisions that by their nature survive termination (IP ownership, indemnification, limitation of liability) will continue to apply',
          ],
        },
      ],
    },
    {
      heading: '14. Modifications to Service and Terms',
      subsections: [
        { sub: '14.1 Changes to the Service', body: 'During the Beta phase, we may add, modify, or remove features at any time without notice. We will communicate significant changes via email or in-app notification where reasonably possible.' },
        {
          sub: '14.2 Changes to These Terms',
          body: 'We may update these Terms from time to time. When we make material changes, we will:',
          list: [
            'Post the updated Terms at auditpros.ai/terms with a new effective date',
            'Notify you via email at least 14 days before the changes take effect (except for changes required by law, which may take effect immediately)',
          ],
          body2: 'Your continued use of AuditPros.ai after the effective date of any updated Terms constitutes your acceptance of the revised Terms.',
        },
      ],
    },
    {
      heading: '15. Third-Party Integrations and Links',
      body: 'AuditPros.ai integrates with or references third-party services including:',
      list: [
        'Tally ERP — for financial data import (governed by Tally\'s own licence terms)',
        'MCA21 Portal, GSTN, and Income Tax Portal — for regulatory reference',
        'Anthropic Claude API — for AI report generation',
      ],
      body2: 'We are not responsible for the availability, accuracy, or practices of any third-party service. Your use of third-party integrations is subject to their respective terms and privacy policies.',
    },
    {
      heading: '16. Force Majeure',
      body: 'We shall not be liable for any failure or delay in performance arising from circumstances beyond our reasonable control, including but not limited to: natural disasters, acts of government or regulatory authority, internet or telecommunications outages, cyberattacks, pandemics, or third-party service failures.',
    },
    {
      heading: '17. Governing Law and Dispute Resolution',
      table: [
        ['Governing Law', 'Laws of India, including the IT Act 2000, DPDP Act 2023, and Indian Contract Act 1872'],
        ['Jurisdiction', 'Courts of Bangalore, Karnataka, India shall have exclusive jurisdiction'],
        ['Dispute Resolution', 'Parties agree to first attempt resolution through good-faith negotiation (30 days), followed by mediation before initiating formal legal proceedings'],
        ['Language', 'These Terms are in English; in case of any conflict, the English version prevails'],
      ],
    },
    {
      heading: '18. Entire Agreement and Severability',
      body: 'These Terms, together with our Privacy Policy, constitute the entire agreement between you and AuditPros.ai with respect to the subject matter herein, and supersede all prior agreements, representations, or understandings whether written or oral.\n\nIf any provision of these Terms is found to be invalid or unenforceable by a court of competent jurisdiction, that provision shall be severed and the remaining provisions shall continue in full force and effect.',
    },
    {
      heading: '19. Contact Us',
      table: [
        ['Legal / Terms Queries', 'Privacy Officer'],
        ['Support', 'support@speedstar.ai'],
        ['Registered Entity', 'Speedstar AI Labs Private Limited, Bangalore, Karnataka, India'],
        ['DPIIT Recognition', 'Recognised Startup under Startup India'],
        ['Response Time', 'Within 5 business days for legal queries; 30 days for formal grievances'],
      ],
    },
  ],
};

function renderBody(text) {
  if (!text) return null;
  return text.split('\n\n').map((para, i) => (
    <p key={i} className='lgl-body'>{para}</p>
  ));
}

function renderSection(sec, idx) {
  return (
    <div key={idx} className='lgl-section'>
      <h2 className='lgl-h2'>{sec.heading}</h2>
      {sec.highlight && <div className='lgl-highlight'>{sec.highlight}</div>}
      {renderBody(sec.body)}
      {sec.list && (
        <ul className='lgl-list'>
          {sec.list.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      )}
      {sec.table && (
        <table className='lgl-table'>
          <tbody>
            {sec.table.map(([a, b], i) => (
              <tr key={i}>
                <td className='lgl-td-key'>{a}</td>
                <td className='lgl-td-val'>{b}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {renderBody(sec.body2)}
      {sec.subsections && sec.subsections.map((sub, si) => (
        <div key={si} className='lgl-subsection'>
          <h3 className='lgl-h3'>{sub.sub}</h3>
          {sub.highlight && <div className='lgl-highlight'>{sub.highlight}</div>}
          {renderBody(sub.body)}
          {sub.list && (
            <ul className='lgl-list'>
              {sub.list.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          )}
          {sub.table && (
            <table className='lgl-table'>
              <tbody>
                {sub.table.map(([a, b], i) => (
                  <tr key={i}>
                    <td className='lgl-td-key'>{a}</td>
                    <td className='lgl-td-val'>{b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {renderBody(sub.body2)}
        </div>
      ))}
    </div>
  );
}

export default function LegalPage({ type, onBack }) {
  const doc = type === 'privacy' ? PRIVACY_POLICY : TERMS_OF_SERVICE;
  return (
    <div className='lgl-root'>
      <div className='lgl-nav'>
        <button className='lgl-back' onClick={onBack}>← Back</button>
        <span className='lgl-nav-brand'>Audit <strong>PrOS</strong></span>
      </div>
      <div className='lgl-content'>
        <div className='lgl-header'>
          <div className='lgl-brand-tag'>AuditPros.ai</div>
          <h1 className='lgl-title'>{doc.title}</h1>
          <p className='lgl-effective'>{doc.effective}</p>
        </div>
        {doc.notice && (
          <div className='lgl-notice-box'>
            <div className='lgl-notice-heading'>{doc.notice.heading}</div>
            <ul className='lgl-notice-list'>
              {doc.notice.items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
        )}
        <div className='lgl-commitments'>
          <div className='lgl-commitments-label'>Key commitments at a glance</div>
          {doc.commitments.map((c, i) => (
            <div key={i} className='lgl-commitment'>
              <span className='lgl-check'>✓</span>{c}
            </div>
          ))}
        </div>
        {doc.sections.map((sec, i) => renderSection(sec, i))}
        <div className='lgl-footer'>
          AuditPros.ai is a product of Speedstar AI Labs Private Limited, a DPIIT Recognised Startup.
        </div>
      </div>
    </div>
  );
}
